import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Commercial Office Move",
  "Biotech & Lab Relocation",
  "Healthcare Relocation",
  "Residential Move",
  "Employee Relocation",
  "Space Planning & Construction",
  "Commercial Flooring",
  "Short-Term Storage",
];

const INITIAL = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  service_type: SERVICE_OPTIONS[0],
  origin_zip: "",
  destination_zip: "",
  move_date: "",
  notes: "",
};

const inputCls =
  "w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      setForm(INITIAL);
      toast.success("Quote request received — our team will reach out within one business day.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call us at (571) 850-9694.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success-panel" className="bg-brand-subtle border border-brand/30 rounded-md p-10 text-center">
        <CheckCircle2 className="mx-auto text-brand mb-4" size={44} />
        <h3 className="font-display font-bold text-2xl text-navy mb-2">Request Received</h3>
        <p className="text-slate-600 text-sm max-w-md mx-auto">
          Thank you — an HLM Moving relocation specialist will contact you within one business day.
          For immediate assistance, call <a href="tel:+15718509694" className="text-brand font-semibold">(571) 850-9694</a>.
        </p>
        <button
          data-testid="quote-submit-another-btn"
          onClick={() => setDone(false)}
          className="mt-6 text-sm font-bold uppercase tracking-wide text-brand hover:text-brand-hover transition-colors"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form data-testid="quote-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Full Name *</label>
        <input data-testid="quote-input-full-name" required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Company / Organization</label>
        <input data-testid="quote-input-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Acme Corp" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Email Address *</label>
        <input data-testid="quote-input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Phone Number *</label>
        <input data-testid="quote-input-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(571) 555-0100" />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Service Category *</label>
        <select data-testid="quote-input-service-type" required className={inputCls} value={form.service_type} onChange={set("service_type")}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Origin Zip Code *</label>
        <input data-testid="quote-input-origin-zip" required className={inputCls} value={form.origin_zip} onChange={set("origin_zip")} placeholder="20148" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Destination Zip Code *</label>
        <input data-testid="quote-input-destination-zip" required className={inputCls} value={form.destination_zip} onChange={set("destination_zip")} placeholder="22201" />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Estimated Move Date *</label>
        <input data-testid="quote-input-move-date" required type="date" className={inputCls} value={form.move_date} onChange={set("move_date")} />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Project Details &amp; Scope</label>
        <textarea data-testid="quote-input-notes" rows={4} className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Square footage, headcount, special equipment, timeline..." />
      </div>
      <div className="sm:col-span-2">
        <button
          data-testid="quote-form-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wide px-10 py-4 rounded-sm transition-colors"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          {submitting ? "Sending..." : "Request My Quote"}
        </button>
      </div>
    </form>
  );
}
